import type React from 'react';
import type { SiteSection } from '../common/interface';
import {
  generateCodeblockSectionId,
  scopeCssToSection,
} from '../utils/cssScoper';
import {
  buildInlineStyles,
  extractSectionAttributes,
} from '../utils/sectionTypeDetector';
import CodeblockError from './CodeblockError';

/**
 * Props interface for CodeblockRenderer component
 */
interface CodeblockRendererProps {
  section: SiteSection;
  showDebugInfo?: boolean;
}

/**
 * Specialized renderer for codeblock sections with EJS processing and CSS scoping
 * Handles EJS template compilation, CSS scoping, and error fallback
 */
const CodeblockRenderer: React.FC<CodeblockRendererProps> = ({
  section,
  showDebugInfo = false,
}) => {
  const uniqueId = generateCodeblockSectionId(section.id);
  const attributes = extractSectionAttributes(section.content);
  const inlineStyles = buildInlineStyles(attributes);
  const processedHtml = section.processedHtml || '';
  const hasEjsError = processedHtml.includes('class="ejs-error"');

  try {
    if (hasEjsError) {
      return (
        <CodeblockError
          message="Template processing failed during server-side rendering"
          sectionId={section.id}
          showDetails={showDebugInfo}
        />
      );
    }

    let scopedCss = '';
    if (section.css?.trim()) {
      const cssResult = scopeCssToSection(section.css, uniqueId);

      if (!cssResult.success) {
        // Continue with unstyled content rather than failing completely
      } else {
        scopedCss = cssResult.scopedCss || '';
      }
    }

    const existingStyle = section.content.match(/style="([^"]*)"/)?.[1] || '';
    const combinedStyles = [existingStyle, inlineStyles]
      .filter(Boolean)
      .join('; ');

    return (
      <>
        {scopedCss && (
          <style
            // biome-ignore lint/security/noDangerouslySetInnerHtml: CSS content processed and scoped server-side
            dangerouslySetInnerHTML={{ __html: scopedCss }}
            data-section-id={section.id}
            suppressHydrationWarning
          />
        )}

        <div
          id={uniqueId}
          className="codeblock-section"
          data-section-id={section.id}
          data-type="codeblock"
        >
          {processedHtml && (
            <div
              className="codeblock-processed-content"
              // biome-ignore lint/security/noDangerouslySetInnerHtml: EJS content processed server-side with trusted context
              dangerouslySetInnerHTML={{ __html: processedHtml }}
              suppressHydrationWarning
            />
          )}

          {!processedHtml && (
            <div
              className="codeblock-original-content"
              // biome-ignore lint/security/noDangerouslySetInnerHtml: Content pre-sanitized by server API
              dangerouslySetInnerHTML={{ __html: section.content }}
              suppressHydrationWarning
            />
          )}

          {showDebugInfo && process.env.NODE_ENV === 'development' && (
            <div
              className="codeblock-debug"
              style={{
                marginTop: '8px',
                padding: '8px',
                backgroundColor: '#f3f4f6',
                borderRadius: '4px',
                fontSize: '12px',
                fontFamily: 'monospace',
                border: '1px solid #d1d5db',
              }}
            >
              <strong>Debug Info:</strong>
              <br />
              Section ID: {section.id}
              <br />
              Unique ID: {uniqueId}
              <br />
              Has EJS: {Boolean(section.ejs?.trim())}
              <br />
              Has CSS: {Boolean(section.css?.trim())}
              <br />
              Has Processed HTML: {Boolean(section.processedHtml)}
              <br />
              Processed HTML Length: {processedHtml.length}
            </div>
          )}
        </div>
      </>
    );
  } catch {
    return (
      <CodeblockError
        message="An unexpected error occurred while processing this section"
        sectionId={section.id}
        showDetails={showDebugInfo}
      />
    );
  }
};

/**
 * Utility function to parse inline styles string into React style object
 * Handles basic CSS property conversion
 */
const parseInlineStyles = (styleString: string): React.CSSProperties => {
  const styles: React.CSSProperties = {};

  if (!styleString) return styles;

  try {
    const declarations = styleString.split(';').filter(Boolean);

    for (const declaration of declarations) {
      const [property, value] = declaration.split(':').map(s => s.trim());

      if (property && value) {
        const camelProperty = property.replace(/-([a-z])/g, (_, letter) =>
          letter.toUpperCase(),
        );

        (styles as Record<string, string>)[camelProperty] = value;
      }
    }
  } catch {}

  return styles;
};

export default CodeblockRenderer;
export type { CodeblockRendererProps };
